/**
 * De dónde vino el visitante — lo que decide a quién se le comisiona.
 *
 * El asistente se promociona por su cuenta, pero también desde las landing de
 * cada sucursal (`uvaconline.com/uvac-suc-queretaro-norte`). Cuando alguien
 * llega, arma su viaje y deja su teléfono, la pregunta de negocio es quién lo
 * mandó: esa sucursal cobra por haberlo traído.
 *
 * Sin esto, todos los prospectos se ven iguales y nadie puede cobrar nada.
 *
 * ## La página que lo mandó ES el identificador
 *
 * Son casi veinte sucursales y cada una tiene su landing. Pedirle al equipo de
 * web que invente y mantenga un código por sucursal es pedirles que se
 * equivoquen: la ruta de la página ya las distingue una por una y ya existe.
 *
 * Por eso el botón manda su propia ruta (`?de=/uvac-suc-queretaro-norte`) y su
 * título (`?ti=UVAC | Suc. Querétaro Norte`). Un mismo pedazo de código en el
 * layout, idéntico en las veinte landings, sin nada que configurar por página y
 * sin nada que se pueda escribir mal.
 *
 * La ruta es la que cuadra la comisión —es estable y única—; el título es para
 * leerlo sin traducir nada.
 *
 * ## Ojo con el referente: el navegador NO manda la ruta
 *
 * Sería natural pensar que basta con mirar de dónde venía el navegador. No
 * basta: la política que traen hoy los navegadores por omisión
 * (`strict-origin-when-cross-origin`) manda **solo el dominio** cuando se salta
 * a otro sitio. De `uvaconline.com/uvac-suc-queretaro-norte` llega
 * `https://uvaconline.com/` — que no dice qué sucursal.
 *
 * Por eso el `?de=` no es un adorno, es el mecanismo. El referente se guarda
 * igual, y si por lo que sea llega con ruta, cuenta; pero no se depende de él.
 *
 * ## La regla: gana el PRIMER origen identificable
 *
 * No el último. Quien trajo a la persona es quien hizo el trabajo; si después
 * vuelve escribiendo la dirección a mano, eso no le quita el mérito a nadie.
 *
 * Con un matiz que importa: una visita directa **no tiene origen**, así que no
 * bloquea. Si alguien entra directo hoy y mañana llega por la landing de
 * Querétaro, esa landing se lleva el crédito — no había nada antes que
 * proteger. Solo el primer origen CON fuente queda fijo.
 *
 * ## Lo que esto NO es
 *
 * No es un rastreador. No guarda huella del navegador, ni cookies de terceros,
 * ni la dirección completa con sus parámetros. Solo: de qué página del sitio
 * salió, cómo se llama esa página, y qué campaña la trajo.
 */

export interface Origen {
  /**
   * La ruta de la página que lo mandó: `/uvac-suc-queretaro-norte`.
   *
   * Es el identificador que cuadra la comisión. Sin dominio y sin parámetros:
   * la ruta sola es estable, única por sucursal y ya existe — nadie tiene que
   * inventarla ni mantenerla.
   */
  pagina?: string
  /** El título de esa página, para leerlo: `UVAC | Suc. Querétaro Norte`. */
  titulo?: string
  /**
   * Código de sucursal, cuando alguien lo pone a mano (`?suc=`). Opcional: la
   * página ya identifica. Sirve para casos sueltos —un asesor, un volante— que
   * no tienen landing propia.
   */
  sucursal?: string
  /** Campaña, en el vocabulario de siempre (`utm_*`). */
  fuente?: string
  medio?: string
  campana?: string
  contenido?: string
  termino?: string
  /** De qué sitio venía el navegador. Casi siempre solo el dominio: ver arriba. */
  referente?: string
  /** Cuándo se capturó, en ISO. Para poder decir "llegó el 12 de marzo". */
  capturadoEn?: string
}

/** Un parámetro limpio: recortado, acotado y sin cosas raras. */
function leerParam(p: URLSearchParams, ...nombres: string[]): string | undefined {
  for (const n of nombres) {
    const v = p.get(n)?.trim()
    // 120 caracteres es de sobra para una campaña. Más largo es un accidente o
    // alguien probando qué pasa si mete media novela en la URL.
    if (v) return v.slice(0, 120)
  }
  return undefined
}

/**
 * Deja una ruta en su forma canónica: `/uvac-suc-queretaro-norte`.
 *
 * Se aceptan las tres formas en que el equipo de web puede mandarla —la URL
 * completa, la ruta con parámetros, o la ruta pelona— porque las tres son
 * razonables y ninguna merece una discusión. De las tres nos quedamos con la
 * ruta: el dominio es siempre el mismo y los parámetros no nombran a nadie
 * (ahí viajan búsquedas y a veces datos personales).
 *
 * La portada (`/`) devuelve `undefined`: llegar desde la home no dice de qué
 * sucursal salió, y guardarlo solo ensuciaría el reporte.
 */
function comoRuta(bruto: string | undefined): string | undefined {
  if (!bruto) return undefined
  let v = bruto.trim()
  if (!v) return undefined
  v = v.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '')  // se va el dominio
  v = v.split(/[?#]/)[0]!                              // se van los parámetros
  v = v.replace(/\/+$/, '')                            // se va la diagonal final
  if (!v) return undefined
  if (!v.startsWith('/')) v = `/${v}`
  return v.slice(0, 120)
}

/**
 * De qué sitio venía el navegador.
 *
 * Casi siempre será solo el dominio: la política por omisión de los navegadores
 * recorta la ruta al saltar a otro sitio. Se guarda igual porque distingue
 * "vino del sitio oficial" de "vino de Facebook", y porque cuando sí trae ruta
 * es una atribución gratis.
 *
 * Se descarta el propio sitio: navegar entre nuestras páginas no es un origen,
 * es la misma visita.
 */
function referenteDe(referrer: string, aqui: string): string | undefined {
  if (!referrer) return undefined
  try {
    const r = new URL(referrer)
    if (r.origin === new URL(aqui).origin) return undefined
    return `${r.origin}${r.pathname}`.replace(/\/$/, '') || undefined
  } catch {
    return undefined
  }
}

/**
 * Lee el origen de la dirección con la que se entró y del referente.
 *
 * `null` cuando no hay NADA que registrar —una visita directa, sin parámetros
 * ni referente— porque un objeto vacío en la base de datos solo estorba.
 */
export function leerOrigen(href: string, referrer = ''): Origen | null {
  let p: URLSearchParams
  try {
    p = new URL(href).searchParams
  } catch {
    return null
  }

  const origen: Origen = {}

  // La página que lo mandó. Si el botón no la trajo, se intenta con la ruta del
  // referente — que casi nunca llega, pero cuando llega es gratis.
  const referente = referenteDe(referrer, href)
  const pagina = comoRuta(leerParam(p, 'de', 'pagina', 'landing'))
    ?? comoRuta(referente)
  if (pagina) origen.pagina = pagina

  const titulo = leerParam(p, 'ti', 'titulo')
  if (titulo) origen.titulo = titulo

  const sucursal = leerParam(p, 'suc', 'sucursal', 'ref')
  if (sucursal) origen.sucursal = sucursal

  const fuente = leerParam(p, 'utm_source', 'fuente')
  if (fuente) origen.fuente = fuente
  const medio = leerParam(p, 'utm_medium', 'medio')
  if (medio) origen.medio = medio
  const campana = leerParam(p, 'utm_campaign', 'campana')
  if (campana) origen.campana = campana
  const contenido = leerParam(p, 'utm_content')
  if (contenido) origen.contenido = contenido
  const termino = leerParam(p, 'utm_term')
  if (termino) origen.termino = termino

  if (referente) origen.referente = referente

  return Object.keys(origen).length ? origen : null
}

/**
 * ¿Este origen sirve para comisionar?
 *
 * Hace falta algo que nombre a alguien: la página que lo mandó, un código de
 * sucursal o una campaña. Un referente pelón —`https://uvaconline.com/`, que es
 * lo que manda el navegador de fábrica— dice que vino del sitio y nada más:
 * sirve para entender, no para pagar.
 *
 * El título tampoco cuenta por sí solo. Se puede editar en cualquier momento
 * sin que nadie lo note; la ruta no.
 */
export function sirveParaComisionar(o: Origen | null | undefined): boolean {
  return !!(o && (o.pagina || o.sucursal || o.fuente || o.campana))
}
